import { SelectV2Option as SelectV2OptionType } from './types';
import { TooltipV2 } from '../tooltip/TooltipV2';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import {
    FloatingPortal,
    size as sizeFunction,
    autoUpdate,
    flip,
    useFloating,
    hide,
    shift,
    ElementRects,
    Elements,
    offset,
} from '@floating-ui/react';
import { Combobox } from '@headlessui/react';
import { FixedSizeList } from 'react-window';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Spinner from '../spinner/Spinner';
import BadgeV3 from '../badge/BadgeV3';
import SelectV2Option from './SelectV2Option';

// TO-DO : expose a ref

export const style = {
    fontSize: {
        standard: 'text-sm',
        small: 'text-xs placeholder:text-xs',
    },
    iconSize: {
        standard: 'w-5 h-5',
        small: 'w-4 h-4',
    },
    borderStyle: {
        border: 'border rounded-md dark:bg-white/5',
        underline: 'border-0 border-b rounded-none dark:bg-transparent',
        consolidated:
            'border focus-within:border-b-gray-400 focus-within:rounded-none focus-within:rounded-tl-md focus-within:rounded-tr-md dark:bg-transparent',
    },
    borderColor: {
        withError: 'border-red-600 dark:border-red-400 focus-within:outline-none focus-within:ring-0',
        withoutError: 'border-gray-200 dark:border-white/[0.07]',
    },
    focusedStyle: {
        border: 'focus-within:outline-none focus-within:ring-1 focus-within:ring-indigo-600 focus-within:border-indigo-600 focus-within:dark:border-indigo-400',
        underline: 'focus-within:outline-none focus-within:ring-0 focus-within:border-indigo-600 focus-within:dark:border-indigo-400',
        consolidated: 'focus-within:outline-none focus-within:ring-0 focus-within:border-indigo-600 focus-within:dark:border-indigo-400',
    },
};

export type SelectV2Props = {
    size?: 'small' | 'standard';
    borderStyle?: 'border' | 'underline';
    options?: SelectV2OptionType[];
    isCreatable?: boolean;
    isLoading?: boolean;
    isOptional?: boolean;
    isMulti?: boolean;
    disabledHint?: string;
    title?: string;
    description?: string;
    errorMessage?: string;
    emptyMessage?: string;
    hint?: string;
    showMenuAsRelative?: boolean;
    newOptionActionLabel?: string;
    value?: SelectV2OptionType[];
    keepSelectedOptions?: boolean;
    allowOverflow?: boolean;
    expandMenu?: boolean;
    consolidated?: boolean;
    hideSelectedOptions?: boolean;
    onSearch?: (query: string) => void;
    onChange?: (options: SelectV2OptionType[]) => void;
    onScrollEnd?: () => void;
    onBlur?: () => void;
    root?: React.MutableRefObject<HTMLDivElement>;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'value' | 'onChange' | 'onBlur'>;

const DEBOUNCE_RATE = 250;

const SelectV2 = React.forwardRef(function SelectV2(
    {
        size = 'standard',
        borderStyle = 'border',
        options,
        isCreatable = false,
        isLoading = false,
        isOptional = false,
        isMulti = false,
        disabledHint,
        title,
        description,
        errorMessage,
        emptyMessage = 'No options',
        hint,
        showMenuAsRelative = false,
        keepSelectedOptions = false,
        allowOverflow = false,
        newOptionActionLabel = 'Create',
        value = [],
        expandMenu = false,
        consolidated = false,
        hideSelectedOptions = false,
        onSearch,
        onChange,
        onScrollEnd,
        onBlur,
        root,
        ...props
    }: SelectV2Props,
    ref: React.Ref<HTMLInputElement>
): JSX.Element {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const outerDivRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLUListElement>(null);
    const [search, setSearch] = useState('');
    const [showInput, setShowInput] = useState(true);
    const [filteredOptions, setFilteredOptions] = useState<SelectV2OptionType[]>([]);
    const [inputFocused, setInputFocused] = useState(false);
    const placeholder = props.placeholder || 'Select...';

    const OPTION_SIZE = 36;
    const MAX_DROPDOWN_HEIGHT = 250;

    const { refs, floatingStyles, middlewareData } = useFloating({
        whileElementsMounted: autoUpdate,
        placement: 'bottom',
        middleware: [
            offset(4),
            flip(),
            shift(),
            hide(),
            sizeFunction({
                apply({ rects, elements }: { rects: ElementRects; elements: Elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                    });
                },
            }),
        ],
    });

    useEffect(() => {
        if (inputRef.current) {
            if (search === '') {
                inputRef.current.style.height = size === 'small' ? '16px' : '20px';
            } else {
                inputRef.current.style.height = 'auto';
                inputRef.current.style.height = inputRef.current?.scrollHeight + 'px';
            }
        }
    }, [search, size]);

    function handleDelete(event: React.KeyboardEvent<HTMLTextAreaElement>): void {
        if (event.key === 'Backspace' && search === '' && value.length > 0) {
            onChange(value.slice(0, value.length - 1));
        } else if (event.key === 'Enter' && search && value.find((val) => val.value === search)) {
            setSearch('');
        }
    }

    function handleScroll(event: React.UIEvent<HTMLUListElement, UIEvent>): void {
        const bottom = Math.round(event.currentTarget.scrollHeight - event.currentTarget.scrollTop) === event.currentTarget.clientHeight;
        if (bottom && onScrollEnd) {
            onScrollEnd();
        }
    }

    function handleSelected(val: SelectV2OptionType[]): void {
        if (isMulti) {
            const lastValue = val[val.length - 1];

            if (val.filter((v) => v.value === lastValue.value).length === 2) {
                onChange(val.filter((v) => v.value !== '' && v.value !== lastValue.value));
            } else {
                onChange(val.filter((v) => v.value !== ''));
            }
        } else {
            onChange(val[val.length - 1] ? [val[val.length - 1]] : []);
            buttonRef.current.click();
            if (val.length !== 0) {
                setShowInput(false);
                setTimeout(() => inputRef.current?.blur());
            }
        }
        setSearch('');
    }

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (!(event.target instanceof Element)) {
                return;
            }

            if (optionsRef.current && (optionsRef.current.contains(event.target) || optionsRef.current.isEqualNode(event.target))) {
                return;
            }

            if (outerDivRef.current && !outerDivRef.current.contains(event.target)) {
                if (value.length !== 0) {
                    setShowInput(false);
                }
                setSearch('');
            }
        },
        [value]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);

        return (): void => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect((): void => {
        if (keepSelectedOptions) {
            setFilteredOptions(options);
        } else {
            setFilteredOptions(options.filter((opt) => !value.find((val) => val.value === opt.value)));
        }
    }, [options, keepSelectedOptions, value]);

    useEffect((): void => {
        if (props.autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
        if (expandMenu && inputRef.current) {
            inputRef.current.click();
        }
    }, [props.autoFocus, expandMenu]);

    /**
     * Whenever the value changes don't fire the onChange event until the timeout has completed.
     * Everytime a new value is entered; reset the timeout
     */
    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (search === '' && onSearch) {
            onSearch('');
            return;
        }

        if (search !== undefined && onSearch) {
            timeout = setTimeout(() => onSearch(search), DEBOUNCE_RATE);
        }
        return (): void => timeout && clearTimeout(timeout);
    }, [search, onSearch]);

    const canCreateNewOption = useMemo(
        () => isCreatable && search && !value.find((val) => val.value === search),
        [isCreatable, search, value]
    );

    useEffect(() => {
        if (value.length === 0 && !showInput) {
            setShowInput(true);
        }
    }, [value, showInput]);

    return (
        <div>
            {(title || isOptional) && (
                <label className="flex justify-between mb-1">
                    {title && <span className={`block ${style.fontSize[size]} font-medium text-gray-700 dark:text-gray-200`}>{title}</span>}
                    {isOptional && <span className={`${style.fontSize[size]} text-gray-500 ml-auto`}>Optional</span>}
                </label>
            )}
            {description && (
                <label className={`block ${style.fontSize[size]} font-normal text-gray-600 dark:text-gray-400 mb-1`}>{description}</label>
            )}
            <Combobox disabled={props.disabled} value={value} onChange={handleSelected} multiple>
                <TooltipV2 message={disabledHint} position="right" disabled={!props.disabled || !disabledHint}>
                    <div className="relative" ref={refs.setReference}>
                        <div
                            className={`flex items-center justify-between rounded-md bg-white px-3 ${
                                consolidated ? 'py-1.5' : 'py-2'
                            } text-gray-500 dark:text-gray-200 ${style.fontSize[size]} ${
                                style.borderStyle[consolidated ? 'consolidated' : borderStyle]
                            } ${style.borderColor[errorMessage ? 'withError' : 'withoutError']} ${
                                props.disabled
                                    ? value.length === 0
                                        ? 'opacity-50'
                                        : 'bg-gray-50'
                                    : errorMessage
                                    ? ''
                                    : style.focusedStyle[consolidated ? 'consolidated' : borderStyle]
                            }`}
                            ref={outerDivRef}
                            onClick={(): void => {
                                if (!props.disabled) {
                                    setShowInput(true);
                                    setTimeout(() => {
                                        inputRef.current?.focus();
                                    });
                                    buttonRef.current.click();
                                    setSearch('');
                                }
                            }}
                        >
                            <div
                                tabIndex={-1}
                                className={`flex ${allowOverflow ? 'overflow-hidden' : 'flex-wrap'} items-center cursor-text ${
                                    allowOverflow ? 'w-[80%]' : value.length > 0 ? 'w-[80%]' : 'w-[90%]'
                                } ${isMulti ? 'gap-1 -my-1' : ''} `}
                                onClick={(e): void => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!props.disabled && !showInput) {
                                        setShowInput(true);
                                        inputRef.current?.focus();
                                    }
                                    buttonRef.current.click();
                                    setSearch('');
                                }}
                            >
                                {value.length > 0 &&
                                    !hideSelectedOptions &&
                                    (isMulti ? (
                                        value.map((v) => {
                                            return (
                                                <div className="shrink-0" key={v.value}>
                                                    <BadgeV3
                                                        focusable={false}
                                                        size="small"
                                                        label={v.label}
                                                        color="gray"
                                                        avatar={v.decoration}
                                                        onRemove={
                                                            props.disabled
                                                                ? undefined
                                                                : (e): void => {
                                                                      e.preventDefault();
                                                                      e.stopPropagation();
                                                                      onChange(value.filter((val) => val.value !== v.value));
                                                                      setShowInput(true);
                                                                      inputRef.current?.focus();
                                                                  }
                                                        }
                                                    />
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div
                                            className={`flex gap-2 items-center ${
                                                props.disabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'
                                            }`}
                                        >
                                            {value[0]?.decoration}
                                            <p className={`${allowOverflow ? 'truncate' : 'break-all'}`}>
                                                {value.length === 1 ? value[0].label : ''}
                                            </p>
                                        </div>
                                    ))}

                                <Combobox.Input
                                    as="textarea"
                                    rows={1}
                                    cols={1}
                                    style={{
                                        width: search?.length && search.length + 1 + 'ch',
                                        height: size === 'small' ? '16px' : '20px',
                                    }}
                                    ref={inputRef}
                                    value={search}
                                    onClick={(e: React.MouseEvent<HTMLTextAreaElement, MouseEvent>): void => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!search || search.length === 0) {
                                            buttonRef.current.click();
                                        }
                                    }}
                                    onFocus={(): void => {
                                        setShowInput(true);
                                        setInputFocused(true);
                                    }}
                                    onBlur={(): void => {
                                        setInputFocused(false);
                                        onBlur && onBlur();
                                    }}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                                        if (!isMulti && value.length === 1) {
                                            onChange([]);
                                        }
                                        setSearch(e.target.value === '\n' ? '' : e.target.value);
                                    }}
                                    placeholder={hideSelectedOptions || value.length === 0 ? placeholder : undefined}
                                    className={`${
                                        showInput ? '' : 'w-0 h-0 opacity-0'
                                    } bg-transparent resize-none overflow-hidden grow border-0 m-0 p-0 focus:ring-0 focus:outline-0 ${
                                        style.fontSize[size]
                                    }`}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>): void => handleDelete(e)}
                                />
                            </div>
                            <div className="flex gap-2">
                                {isLoading && (
                                    <div className="flex items-center">
                                        <Spinner size="sm" />
                                    </div>
                                )}
                                {!consolidated && !isLoading && value.length > 0 && !props.disabled && (
                                    <button
                                        className="flex items-center"
                                        disabled={props.disabled}
                                        onClick={(e): void => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onChange([]);
                                            setShowInput(true);
                                            inputRef.current?.focus();
                                        }}
                                    >
                                        <XMarkIcon className={`${style.iconSize[size]} w-4 h-4 text-gray-400`} />
                                    </button>
                                )}
                                <Combobox.Button
                                    className="flex items-center rounded-r-md"
                                    ref={buttonRef}
                                    disabled={props.disabled}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
                                        e.stopPropagation();
                                        setSearch('');
                                        !showInput && setShowInput(true);
                                    }}
                                    onMouseDown={(): void => setInputFocused(true)}
                                >
                                    <ChevronUpDownIcon className={`${style.iconSize[size]} text-gray-400`} />
                                </Combobox.Button>
                            </div>
                        </div>
                        {inputFocused && (
                            <FloatingPortal root={root}>
                                <div
                                    ref={refs.setFloating}
                                    style={{
                                        ...floatingStyles,
                                        zIndex: 999,
                                        visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
                                    }}
                                >
                                    <Combobox.Options
                                        static={isMulti}
                                        ref={optionsRef}
                                        onScroll={(e: React.UIEvent<HTMLUListElement, UIEvent>): void => handleScroll(e)}
                                        onMouseDown={(e: React.MouseEvent<HTMLUListElement, MouseEvent>): void => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        className={`${showMenuAsRelative ? 'relative' : 'absolute'} z-40 ${
                                            consolidated ? 'rounded-none rounded-bl-md rounded-br-md' : 'mt-1 rounded-md'
                                        } max-h-72 w-full overflow-y-auto overflow-x-hidden border border-t-0 bg-white ${
                                            consolidated && inputFocused ? 'border-indigo-600' : 'border-gray-200'
                                        } dark:bg-white/5 dark:border-white/[0.07] py-1 text-base sm:text-sm`}
                                    >
                                        {filteredOptions.length > 0 ? (
                                            <FixedSizeList
                                                height={
                                                    filteredOptions.length * OPTION_SIZE > MAX_DROPDOWN_HEIGHT
                                                        ? MAX_DROPDOWN_HEIGHT
                                                        : filteredOptions.length * OPTION_SIZE
                                                }
                                                width={'100%'}
                                                itemCount={filteredOptions.length}
                                                itemSize={OPTION_SIZE}
                                            >
                                                {({ index, style: listStyle }) => (
                                                    <div style={listStyle}>
                                                        <SelectV2Option
                                                            key={filteredOptions[index].value}
                                                            option={filteredOptions[index]}
                                                            size={size}
                                                            isSelected={
                                                                value.find((val) => val.value === filteredOptions[index].value) !==
                                                                undefined
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </FixedSizeList>
                                        ) : (
                                            <Combobox.Option
                                                value={isCreatable && { label: search, value: search }}
                                                className={`group flex items-center justify-center select-none relative py-2 pl-3 pr-4 ${
                                                    canCreateNewOption
                                                        ? 'cursor-pointer hover:bg-indigo-600 hover:dark:bg-indigo-400'
                                                        : 'cursor-default'
                                                } ${style.fontSize[size]}`}
                                                disabled={!canCreateNewOption}
                                            >
                                                {canCreateNewOption ? (
                                                    <span className="group-hover:text-white group-hover:dark:text-gray-50 text-gray-700 dark:text-gray-200">{`${newOptionActionLabel} '${search}'`}</span>
                                                ) : (
                                                    <span className="text-gray-500 dark:text-white/[0.60]">{emptyMessage}</span>
                                                )}
                                            </Combobox.Option>
                                        )}
                                    </Combobox.Options>
                                </div>
                            </FloatingPortal>
                        )}
                    </div>
                </TooltipV2>
                <div className="flex">
                    {hint && !errorMessage && <p className={`mt-2 ${style.fontSize[size]} text-gray-500`}>{hint}</p>}
                    {errorMessage && <p className={`mt-2 ${style.fontSize[size]} text-red-800 dark:text-red-400`}>{errorMessage}</p>}
                </div>
            </Combobox>
        </div>
    );
});

export default SelectV2;
